import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/adminApi'

type Crud = 'slaPolicies' | 'calendars' | 'routingRules'

type Api<K extends Crud> = (typeof adminApi)[K]
type Item<K extends Crud> = Awaited<ReturnType<Api<K>['list']>>[number]
type Input<K extends Crud> = Parameters<Api<K>['create']>[0]

/** List + create/update/delete for one admin resource; every write refreshes the list and the admin audit. */
export function useAdminResource<K extends Crud>(resource: K, enabled: boolean) {
  const queryClient = useQueryClient()
  const api = adminApi[resource] as unknown as {
    list: () => Promise<Item<K>[]>
    create: (input: Input<K>) => Promise<Item<K>>
    update: (id: string, input: Partial<Input<K>>) => Promise<Item<K>>
    remove: (id: string) => Promise<void>
  }
  const key = ['admin', resource] as const
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key })
    void queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] })
  }
  const list = useQuery({ queryKey: key, queryFn: api.list, enabled, retry: false })
  const save = useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Input<K> }) => (id ? api.update(id, input) : api.create(input)),
    onSuccess: refresh,
  })
  const remove = useMutation({ mutationFn: (id: string) => api.remove(id), onSuccess: refresh })
  return { list, save, remove }
}

export function useAdminAudit(enabled: boolean) {
  return useQuery({ queryKey: ['admin', 'audit'], queryFn: adminApi.adminAudit, enabled, retry: false })
}
